import { useApolloClient, useQuery } from "@apollo/client"
import {
  Card,
  Menu,
  MenuDivider,
  MenuItem,
  Position,
  Tag,
} from "@blueprintjs/core"
import CustomButton from "components/Atoms/CustomButton"
import CustomPopover from "components/Atoms/CustomPopover"
import NewTag from "components/Atoms/NewTag"
import StyledLink from "components/Atoms/StyledLink"
import Error from "components/Common/Error"
import ReactTableHeader from "components/Common/ReactTableHeader"
import TablePagination from "components/Molecules/TablePagination"
import NewResourceButton from "components/Organisms/AddResourceButton"
import DataCell, { Subtitle, Title } from "components/Organisms/table/DataCell"
import SerialColumn from "components/Organisms/table/SerialColumn"
import { VARIETY_DETAILS, VARIETY_LIST } from "graphql/crop.graphql"
import { orderBy } from "lodash-es"
import React from "react"
import { useNavigate, useLocation } from "react-router-dom"
import ReactTable from "react-table"
import AppToaster from "utils/AppToaster"
// Utils
import { flattenID, removeTypename } from "utils/objectUtils"

export default function Crops() {
  const navigate = useNavigate()
  const location = useLocation()
  const client = useApolloClient()
  const parseUrl = new URLSearchParams(location.search)

  const COLUMNS = [
    SerialColumn,
    {
      accessor: "actions",
      maxWidth: 50,

      Cell({ original: { id } }) {
        return (
          <CustomPopover
            position={Position.BOTTOM_LEFT}
            minimal={true}
            content={
              <Menu>
                <MenuItem
                  text="Edit Crop"
                  icon="edit"
                  onClick={e => navigate(id)}
                />
                <MenuItem
                  icon="clipboard"
                  text="Copy Values"
                  onClick={e => copyValues(id)}
                />
                <MenuDivider />
                <MenuItem
                  icon="projects"
                  text="Archive Crop"
                  onClick={e => handleDelete(id)}
                />
              </Menu>
            }
          >
            <CustomButton
              style={{ transform: "rotate(90deg)" }}
              icon="more"
              minimal={true}
            />
          </CustomPopover>
        )
      },
    },
    {
      Header: "Crop",
      accessor: "name",
      width: 200,
      Cell({ value, original }) {
        return (
          <div className="flex items-baseline">
            <DataCell>
              <Title>
                <StyledLink to={original.id}>{value}</StyledLink>
              </Title>
              <Subtitle>{original?.commodity?.name ?? "Na"}</Subtitle>
            </DataCell>
            <NewTag createdAt={original.createdAt} />
          </div>
        )
      },
    },
    {
      Header: "Grade Types",
      accessor: "harvestGrades",
      Cell({ value }) {
        return (
          <span>
            {value &&
              value.map(v => (
                <Tag
                  key={v.id || v.name}
                  className="ml-2"
                  minimal={true}
                  round={true}
                  intent="primary"
                >
                  {v.name}
                </Tag>
              ))}
          </span>
        )
      },
    },
    {
      Header: "Primary Group",
      accessor: "primaryGroup",
      Cell({ value }) {
        return (
          <div>
            <div className="grid grid-cols-12 gap-4">
              <div className="col-span-7">{value && value.name}</div>
              {value && value.codename && (
                <div className="col-span-5">
                  <span className="vt-highlight">
                    ({value && value.codename})
                  </span>
                </div>
              )}
            </div>
          </div>
        )
      },
    },
    {
      Header: "Crop Groups",
      accessor: "groups",
      Cell({ value }) {
        return (
          <span>
            {value.map(v => (
              <Tag
                key={v.id || v.name}
                className="ml-2"
                minimal={true}
                round={true}
                intent="primary"
              >
                {v.name}
              </Tag>
            ))}
          </span>
        )
      },
    },
  ]

  async function copyValues(id) {
    try {
      const {
        data: { varietyDetails },
      } = await client.query({
        query: VARIETY_DETAILS,
        variables: { id },
      })
      AppToaster.show({
        icon: "confirm",
        message: `Crop variety ${varietyDetails.name} copied successfully!`,
        intent: "success",
      })
    } catch (err) {
      AppToaster.show({
        icon: "warning-sign",
        message: `Unable to copy variety !`,
        intent: "warning",
      })
    }
  }

  function handleQuery(query) {
    parseUrl.set("search", query)
    navigate(`${location.pathname}?${parseUrl.toString()}`)
  }

  function handleDelete(id) {}
  // render() {
  const page = parseUrl.get("page") ? parseInt(parseUrl.get("page")) : 0
  const query = parseUrl.get("search") ? parseUrl.get("search") : ""
  const { data, loading, error } = useQuery(VARIETY_LIST, {
    variables: {
      query,
      first: 20,
      skip: page * 20,
    },
  })
  if (error) return <Error />
  let newData = []
  if (data && Object.keys(data).length !== 0) {
    const dataAccess = data && Object.keys(data)[0]
    const cleanData = removeTypename(data && data[dataAccess])
    const flattened = flattenID(cleanData)
    const sortedData = orderBy(flattened, crop => crop.updatedAt, ["desc"])
    newData = sortedData
  }
  return (
    <Card className="p-0">
      <ReactTableHeader
        value={query}
        icon="tree"
        onQueryChange={handleQuery}
        title="Crop Variety"
      >
        <TablePagination
          className="mr-6"
          page={page}
          onPageChange={page => {
            parseUrl.set("page", page.toString())
            parseUrl.set("search", "")
            navigate(`${location.pathname}?${parseUrl.toString()}`)
          }}
        />
        <NewResourceButton module="crop" />
      </ReactTableHeader>
      <ReactTable
        loading={loading}
        showPagination={false}
        className="mt-0 text-center vt-master-table"
        data={newData}
        columns={COLUMNS}
      />
    </Card>
  )
}
